import { useCallback } from "react";
import styled, { keyframes } from "styled-components";

type ThemeToggleProps = {
    isLightMode: boolean;
    onToggle: () => void;
    background?: string;
};

// INDIGO matches the cube mark (see logo/CubeMark.tsx).
const WARM: [string, string, string] = ['#FFA600', '#C97F00', '#E09100'];
const INDIGO: [string, string, string] = ['#58508D', '#3E3866', '#4A4479'];

const HEX_CLIP = 'polygon(25% 6%, 75% 6%, 100% 50%, 75% 94%, 25% 94%, 0% 50%)';

const pulse = keyframes`
    0%, 100% {
        filter:
            drop-shadow(0 0 4px rgba(88, 80, 141, 0.48))
            drop-shadow(0 0 10px rgba(88, 80, 141, 0.38))
            drop-shadow(0 0 19px rgba(88, 80, 141, 0.25))
            drop-shadow(0 0 30px rgba(88, 80, 141, 0.13));
    }
    50% {
        filter:
            drop-shadow(0 0 6px rgba(88, 80, 141, 0.5))
            drop-shadow(0 0 16px rgba(88, 80, 141, 0.48))
            drop-shadow(0 0 29px rgba(88, 80, 141, 0.35))
            drop-shadow(0 0 44px rgba(88, 80, 141, 0.2));
    }
`;

const Label = styled.label`
    position: relative;
    display: block;
    width: 1.7rem;
    height: 1.7rem;
    flex-shrink: 0;
    cursor: pointer;
`;

const Input = styled.input`
    position: absolute;
    inset: 0;
    margin: 0;
    opacity: 0;
    cursor: pointer;
`;

// Bloom traces the hexagon's own silhouette (drop-shadow, not box-shadow) so it
// isn't clipped away by the hex mask on the element underneath it.
const Glow = styled.span<{ $isLightMode: boolean }>`
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    filter:
        drop-shadow(0 0 3px rgba(255, 166, 0, 0.45))
        drop-shadow(0 0 8px rgba(255, 166, 0, 0.33))
        drop-shadow(0 0 15px rgba(255, 166, 0, 0.2));
    transition: filter 0.5s ease;
    animation: ${({ $isLightMode }) => ($isLightMode ? 'none' : pulse)} 2s ease-in-out infinite;
`;

const Hex = styled.span<{ $background?: string }>`
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: ${({ theme, $background }) => $background ?? theme.colour.DARK_GREY};
    clip-path: ${HEX_CLIP};
`;

const Disc = styled.span`
    position: absolute;
    inset: 0;
`;

const SunDisc = styled(Disc)`
    background: radial-gradient(circle at 35% 30%, ${WARM[2]}, ${WARM[0]} 60%, ${WARM[1]} 100%);
`;

const MoonDisc = styled(Disc)<{ $isLightMode: boolean }>`
    background: radial-gradient(circle at 65% 65%, ${INDIGO[2]}, ${INDIGO[0]} 60%, ${INDIGO[1]} 100%);
    transform: translateX(${({ $isLightMode }) => ($isLightMode ? '-100%' : '0')});
    transition: transform 0.5s cubic-bezier(0.65, 0, 0.35, 1);
`;

export default function ThemeToggle({ isLightMode, onToggle, background }: ThemeToggleProps){

    const handleInputChange = useCallback(() => {
        onToggle();
    }, [onToggle])

    return (
        <Label>
            <Input
                type="checkbox"
                checked={!isLightMode}
                onChange={handleInputChange}
                aria-label="Toggle light and dark mode"
            />
            <Glow $isLightMode={isLightMode}>
                <Hex $background={background}>
                    <SunDisc />
                    <MoonDisc $isLightMode={isLightMode} />
                </Hex>
            </Glow>
        </Label>
    )
}
